/**
 * Client-side CPF validator.
 *
 * Mirrors the backend's `@ValidCpf` constraint (modulo-11 algorithm) so the
 * registrations forms can block submission before the network roundtrip.
 * Server-side validation remains the source of truth — this is a UX guard.
 */

const CPF_LENGTH = 11;
const FIRST_DV_INDEX = 9;
const SECOND_DV_INDEX = 10;
const FIRST_DV_WEIGHT_START = 10;
const SECOND_DV_WEIGHT_START = 11;
const MOD_11 = 11;
const TEN = 10;

const stripNonDigits = (raw: string): string => raw.replace(/\D/g, '');

const isAllSameDigit = (digits: string): boolean => /^(\d)\1{10}$/.test(digits);

const computeDigit = (digits: string, length: number, weightStart: number): number => {
  let sum = 0;
  for (let i = 0; i < length; i += 1) {
    const ch = digits.charAt(i);
    sum += Number(ch) * (weightStart - i);
  }
  const dv = (sum * TEN) % MOD_11;
  return dv === TEN ? 0 : dv;
};

/**
 * Returns `true` when `raw` is a valid CPF (after stripping any mask).
 * Empty / malformed input returns `false`.
 */
export function isValidCpf(raw: string): boolean {
  const digits = stripNonDigits(raw);
  if (digits.length !== CPF_LENGTH) return false;
  if (isAllSameDigit(digits)) return false;

  const d1 = computeDigit(digits, FIRST_DV_INDEX, FIRST_DV_WEIGHT_START);
  if (d1 !== Number(digits.charAt(FIRST_DV_INDEX))) return false;

  const d2 = computeDigit(digits, SECOND_DV_INDEX, SECOND_DV_WEIGHT_START);
  return d2 === Number(digits.charAt(SECOND_DV_INDEX));
}

/** Strips any mask, returning only digits. */
export const onlyDigits = (raw: string | null | undefined): string =>
  stripNonDigits(raw ?? '');

/** Applies the canonical CPF mask `000.000.000-00` (best effort, partial input ok). */
export function maskCpf(raw: string): string {
  const digits = stripNonDigits(raw).slice(0, CPF_LENGTH);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

/** Applies the canonical Brazilian phone mask. Accepts 10 or 11 digits. */
export function maskPhone(raw: string): string {
  const digits = stripNonDigits(raw).slice(0, 11);
  if (digits.length <= 10) {
    return digits.replace(/(\d{2})(\d{4})(\d)/, '($1) $2-$3');
  }
  return digits.replace(/(\d{2})(\d{5})(\d)/, '($1) $2-$3');
}

const CELLPHONE_LENGTH = 11;

/**
 * Returns `true` when `raw` is a valid Brazilian cellphone (FAV-165): after
 * stripping the mask it must be 11 digits — a 2-digit DDD (not starting with 0)
 * plus the mandatory leading `9` of the subscriber number. Mirrors the backend
 * `@ValidPhone` constraint; server-side validation remains the source of truth.
 */
export function isValidBrCellphone(raw: string): boolean {
  const digits = stripNonDigits(raw);
  if (digits.length !== CELLPHONE_LENGTH) return false;
  if (digits.charAt(0) === '0') return false;
  return digits.charAt(2) === '9';
}

/** Applies the canonical CEP mask `00000-000`. */
export function maskCep(raw: string): string {
  return stripNonDigits(raw).slice(0, 8).replace(/(\d{5})(\d)/, '$1-$2');
}

/* ------------------------------------------------------------------ */
/* CNPJ — mirrors the backend `@ValidCnpj` constraint (modulo-11).     */
/* ------------------------------------------------------------------ */

const CNPJ_LENGTH = 14;
const CNPJ_FIRST_DV_INDEX = 12;
const CNPJ_SECOND_DV_INDEX = 13;
const CNPJ_FIRST_WEIGHTS = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const;
const CNPJ_SECOND_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const;

const isAllSameDigit14 = (digits: string): boolean => /^(\d)\1{13}$/.test(digits);

const computeCnpjDigit = (digits: string, weights: readonly number[]): number => {
  let sum = 0;
  for (let i = 0; i < weights.length; i += 1) {
    sum += Number(digits.charAt(i)) * (weights[i] ?? 0);
  }
  const mod = sum % MOD_11;
  return mod < 2 ? 0 : MOD_11 - mod;
};

/**
 * Returns `true` when `raw` is a valid CNPJ (after stripping any mask).
 * Empty / malformed input returns `false`. Server-side `@ValidCnpj` remains
 * the source of truth — this is a UX guard mirroring the CPF helper above.
 */
export function isValidCnpj(raw: string): boolean {
  const digits = stripNonDigits(raw);
  if (digits.length !== CNPJ_LENGTH) return false;
  if (isAllSameDigit14(digits)) return false;

  const d1 = computeCnpjDigit(digits, CNPJ_FIRST_WEIGHTS);
  if (d1 !== Number(digits.charAt(CNPJ_FIRST_DV_INDEX))) return false;

  const d2 = computeCnpjDigit(digits, CNPJ_SECOND_WEIGHTS);
  return d2 === Number(digits.charAt(CNPJ_SECOND_DV_INDEX));
}

/** Applies the canonical CNPJ mask `00.000.000/0000-00` (best effort, partial input ok). */
export function maskCnpj(raw: string): string {
  const digits = stripNonDigits(raw).slice(0, CNPJ_LENGTH);
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}
